import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { User } from '../graphql/API';
import { reducersState } from '../redux/reducers';
import { setSelectedClass } from '../redux/actions/class';
import { addSchoolMonthlyReport, setSelectedMonthlyReport } from '../redux/actions/fullReport';
import { SchoolStatisticServiceWrapper, SchoolStatistic } from '../modules/Report/SchoolReport/SchoolStatisticServiceWrapper';
import { ClassesGridSectionContainer } from '../modules/FullReportPage/ClassesGridSectionContainer';
// @ts-ignore: TS2459 — we know it's there at runtime
import {
  DashboardStatsBar,
  AttendanceSection,
  PerformanceChartsSection,
} from '@PikaDoApp/pikado-web-dsm';
import SimpleLoader from '../components/SimpleLoader/SimpleLoader';
import {ReportHeader} from '../components/ReportHeader';
import {ReportSidebar} from '../components/ReportSidebar';
import {ReportFooter} from '../components/ReportFooter';

const Loading: React.FC<{title?: string}> = ({title}) => (
  <SimpleLoader isLoading={true} title={title || 'Loading...'} />
);

const schoolStatisticService = new SchoolStatisticServiceWrapper();


// Month configuration: Fetch current month for school attendance data
const CURRENT_MONTH = moment().format('YYYY-MM');



export const SchoolHomePage: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Redux state
  const classes = useSelector((state: reducersState) => state.user?.classes);
  const school = useSelector((state: reducersState) => state.user?.school);
  const user: User = useSelector((state: any) => state.user.user);
  const [participation , setParticipation] = useState(100);
  const selectedClassIdFromRedux = useSelector(
    (state: reducersState) => state.classState?.selectedClassId,
  );
  // Determine active class ID (use Redux value or default to first class)
  const activeClassId = selectedClassIdFromRedux || classes?.[0]?.id;
  const selectedClassMonthlyReportArr = useSelector(
    (state: reducersState) => state.fullReport?.selectedClassMonthlyReportArr || [],
  );



  // Sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(true);

  // Component state
  const [lastSixMonthsPerformance, setLastSixMonthsPerformance] = useState<any>();
  const [selectedMonth, setSelectedMonth] = useState<string>(''); // Initialize empty, will be set from Redux or current month
  const [schoolStatistic, setSchoolStatistic] = useState<SchoolStatistic | null>(null);
  const [isLoadingMonthlyReport, setIsLoadingMonthlyReport] = useState(true);


  // Check if route is /detailed - if so, show class data; otherwise show school data
  const isDetailedRoute = location.pathname.includes('/detailed');
  const showSchoolData = !isDetailedRoute;



  // Fetch school statistic (replaces getSchoolLastSixMonthsPerformance and getSchoolLifetimeSummary)
  useEffect(() => {
    const schoolId = school?.id;
    if (!schoolId || !showSchoolData) return;

    const fetchSchoolStatistic = async () => {
      try {
        setIsLoadingMonthlyReport(true);
        const statistic = await schoolStatisticService.getSchoolStatistic(schoolId);
        setSchoolStatistic(statistic);
        // Transform and set monthly performance data
        if (statistic?.pastMonthlyAverageList) {
          const transformed = statistic.pastMonthlyAverageList
            .map(item => ({
              averagePerformance: item.average,
              month: item.month
            }))
            .sort((a, b) => moment(a.month).valueOf() - moment(b.month).valueOf());
          setLastSixMonthsPerformance(transformed);
        }
        setIsLoadingMonthlyReport(false);
      } catch (error) {
        console.error('Error fetching school statistic:', error);
        setSchoolStatistic(null);
      } finally {
        setIsLoadingMonthlyReport(false);
      }
    };

    fetchSchoolStatistic();
  }, [school?.id, showSchoolData]);

  // Fetch school monthly report for current month on initialization
  useEffect(() => {
    const schoolId = school?.id;
    if (!schoolId || !showSchoolData) return;

    // Only fetch if current month report doesn't exist in Redux
    const existingReport = selectedClassMonthlyReportArr.find(
      (r) => r.month === CURRENT_MONTH
    );

    if (!existingReport) {
      const fetchSchoolMonthlyReport = async () => {
        try {
          setIsLoadingMonthlyReport(true);
          const report = await schoolStatisticService.getSchoolReportsByMonth(
            CURRENT_MONTH,
            schoolId
          );
          // Add report to Redux with selected = true
          dispatch(addSchoolMonthlyReport(report, true));
        } catch (error) {
          console.error('Error fetching school monthly report:', error);
        } finally {
          setIsLoadingMonthlyReport(false);
        }
      };

      fetchSchoolMonthlyReport();
  }
}, [school?.id, showSchoolData]);



  // Sync Redux with active class on initial load
  useEffect(() => {
    if (classes?.length > 0 && !selectedClassIdFromRedux && activeClassId) {
      dispatch(setSelectedClass(activeClassId));
    }
  }, [classes, selectedClassIdFromRedux, activeClassId, dispatch]);


  // Generate month options for dropdown - includes current month + historical months
  const monthOptions = useMemo(() => {
    const options = [];

    // Add current month
    options.push({
      label: moment(CURRENT_MONTH).format('MMMM YYYY'),
      value: CURRENT_MONTH,
    });

    // Add historical months from lastSixMonthsPerformance
    if (lastSixMonthsPerformance && lastSixMonthsPerformance.length > 0) {
      const historicalOptions = lastSixMonthsPerformance
        .map((monthData: any) => ({
          label: moment(monthData.month).format('MMMM YYYY'),
          value: moment(monthData.month).format('YYYY-MM'),
        }))
        .filter((opt: any) => opt.value !== CURRENT_MONTH) // Don't duplicate current month
        .reverse();

      options.push(...historicalOptions);
    }

    return options;
  }, [lastSixMonthsPerformance]);



  // Month change handler for Performance section
  const handleMonthChange = useCallback(async (month: string) => {
    setSelectedMonth(month);

    const schoolId = school?.id;
    if (!schoolId) return;

    // Check if the selected month exists in Redux
    const existingReport = selectedClassMonthlyReportArr.find(
      (r) => r.month === month
    );
    if (existingReport) {
      // Month exists, just update selection
      dispatch(setSelectedMonthlyReport(month));
    } else {
      // Month doesn't exist, fetch from API
      try {
        setIsLoadingMonthlyReport(true);
        const report = await schoolStatisticService.getSchoolReportsByMonth(
          month,
          schoolId
        );
        // Add report to Redux with selected = true
        dispatch(addSchoolMonthlyReport(report, true));
      } catch (error) {
        console.error('Error fetching school monthly report for month:', month, error);
      } finally {
        setIsLoadingMonthlyReport(false);
      }
    }
  }, [school?.id, selectedClassMonthlyReportArr, dispatch]);



  // Get the selected monthly report from Redux
  const selectedReport = useMemo(() => {
    return selectedClassMonthlyReportArr.find((r) => r.selected);
  }, [selectedClassMonthlyReportArr]);

  // Initialize selectedMonth from Redux selectedReport or fallback to CURRENT_MONTH
  useEffect(() => {
    if (!selectedMonth && showSchoolData) {
      // On initial load, sync with Redux or use current month
      const initialMonth = selectedReport?.month || CURRENT_MONTH;
      setSelectedMonth(initialMonth);
    }
  }, [selectedMonth, selectedReport, showSchoolData]);

  // Transform schoolMonthlyReport data to match AttendanceSection format
  const schoolAttendanceData = useMemo(() => {
    if (!selectedReport) return null;

    // Validate and ensure schoolDailyRecord is an array before processing
    const dailyRecord = Array.isArray(selectedReport.schoolDailyRecord)
      ? selectedReport.schoolDailyRecord
      : [];

    return {
      id: selectedReport.id,
      month: selectedReport.month,
      schoolId: selectedReport.schoolId,
      totalExercises: Number.isFinite(selectedReport.totalExercisesThisMonth)
        ? selectedReport.totalExercisesThisMonth
        : 0,
      totalStudents: Number.isFinite(selectedReport.totalStudentsEngagedThisMonth)
        ? selectedReport.totalStudentsEngagedThisMonth
        : 0,
      createdAt: selectedReport.createdAt,
      updatedAt: selectedReport.updatedAt,
      dailyRecords: dailyRecord.filter(
        (record: any) =>
          record &&
          Number.isFinite(record.exercisesAnsweredCount) &&
          record.exercisesAnsweredCount > 0
      ).map((record: any) => ({
        date: new Date(record.date).toISOString().split('T')[0],
        exercisesCount: record.exercisesAnsweredCount ?? 0,
        studentsCount: record.studentsEngagedCount ?? 0,
      }))
    };

  }, [selectedReport]);

  // Calculate participation percentage
  useEffect(() => {
    if (selectedReport?.totalStudentsEngagedThisMonth && schoolStatistic?.totalStudentsInSchool && schoolStatistic.totalStudentsInSchool > 0) {
      const calculatedParticipation =
        (selectedReport.totalStudentsEngagedThisMonth / schoolStatistic.totalStudentsInSchool) * 100;

      // Ensure we only set valid, finite numbers
      if (Number.isFinite(calculatedParticipation)) {
        setParticipation(Math.round(calculatedParticipation));
      } else {
        setParticipation(0);
      }
    } else {
      setParticipation(0);
    }
  }, [selectedReport?.totalStudentsEngagedThisMonth, schoolStatistic?.totalStudentsInSchool]);

  return (
    <>
      <div className="flex h-screen overflow-hidden bg-pikadoWhiteSmoke relative">
        {/* Loading Overlay */}
        {isLoadingMonthlyReport && (
          <div className="absolute inset-0 bg-white bg-opacity-75 z-50 flex items-center justify-center">
            <Loading title="Loading your report..." />
          </div>
        )}

        {/* Sidebar */}
        <ReportSidebar
          isMobileOpen={isMobileSidebarOpen}
          onMobileClose={() => setIsMobileSidebarOpen(false)}
          isCollapsed={isDesktopSidebarCollapsed}
          onToggleCollapse={() => setIsDesktopSidebarCollapsed(!isDesktopSidebarCollapsed)}
        />

        {/* Main content area */}
        <div className="flex flex-col flex-1 overflow-hidden">
            {/* Sticky header + stats bar */}
            <div className="sticky top-0 z-30">
              <ReportHeader
                school={school}
                schoolName={school?.name || ''}
                studentCount={null}
                userName={user?.firstName || ''}
                onMobileMenuClick={() => setIsMobileSidebarOpen(true)}
              />

              <DashboardStatsBar
                studentCount={schoolStatistic?.totalStudentsInSchool || 0}
                performancePercentage={
                  schoolStatistic?.schoolLifeTimeAverage && Number.isFinite(schoolStatistic.schoolLifeTimeAverage)
                    ? Number(schoolStatistic.schoolLifeTimeAverage.toFixed(2))
                    : 0
                }
                participationPercentage={Number.isFinite(participation) ? participation : 0}
              />
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              <main className="flex flex-col gap-8 p-4 md:p-6 lg:p-8">
                {/* Attendance Section */}
                <AttendanceSection schoolAttendanceData={schoolAttendanceData} />

                {/* Performance Charts */}
                <PerformanceChartsSection
                  averagePercentage={
                    selectedReport?.schoolMonthlyAverage && Number.isFinite(selectedReport.schoolMonthlyAverage)
                      ? Number(selectedReport.schoolMonthlyAverage.toFixed(2))
                      : 0
                  }
                  historicalPerformanceData={lastSixMonthsPerformance || []}
                  monthOptions={monthOptions}
                  selectedMonth={selectedMonth}
                  onMonthChange={handleMonthChange}
                  donutTitle="School Performance"
                  areaChartTitle="Previous Months Performance"
                  areaChartFillColor="rgba(116, 187, 231, 0.75)"
                  areaChartStrokeColor="#74BBE7"
                />

                {/* Classes Section */}
                <ClassesGridSectionContainer
                  classes={classes}
                  monthOptions={monthOptions}
                  selectedMonth={selectedMonth}
                  onMonthChange={handleMonthChange}
                  showSchoolData={showSchoolData}
                  schoolId={school?.id || ''}
                />

                {/* Footers */}
                <ReportFooter />
              </main>
            </div>
          </div>
        </div>
    </>
  );
};

export default SchoolHomePage;
